import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Search, Filter, Download, Plus, Facebook, Instagram, MessageCircle, Mail } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const Contacts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  // Hardcoded contacts to avoid stack issues
  const contacts = [
    {
      id: '1',
      name: 'John Doe',
      email: 'john@example.com',
      platform: 'messenger',
      tags: ['customer', 'vip'],
      lastInteraction: '2025-01-15',
      status: 'subscribed'
    },
    {
      id: '2',
      name: 'Jane Smith',
      email: 'jane@example.com',
      platform: 'instagram',
      tags: ['lead'],
      lastInteraction: '2025-01-14',
      status: 'subscribed'
    },
    {
      id: '3',
      name: 'Mike Johnson',
      email: 'mike@example.com',
      platform: 'whatsapp',
      tags: ['customer'],
      lastInteraction: '2025-01-13',
      status: 'unsubscribed'
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contacts</h1>
            <p className="text-gray-600 mt-1">Manage your subscribers and leads</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">3</p>
                <p className="text-sm text-gray-600 mt-1">Total Contacts</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">2</p>
                <p className="text-sm text-gray-600 mt-1">Subscribed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">1</p>
                <p className="text-sm text-gray-600 mt-1">Unsubscribed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">78%</p>
                <p className="text-sm text-gray-600 mt-1">Engagement Rate</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant={selectedPlatform === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPlatform('all')}
                >
                  All
                </Button>
                <Button
                  variant={selectedPlatform === 'messenger' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPlatform('messenger')}
                >
                  <Facebook className="h-4 w-4 mr-1" />
                  Messenger
                </Button>
                <Button
                  variant={selectedPlatform === 'instagram' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPlatform('instagram')}
                >
                  <Instagram className="h-4 w-4 mr-1" />
                  Instagram
                </Button>
                <Button
                  variant={selectedPlatform === 'whatsapp' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPlatform('whatsapp')}
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  WhatsApp
                </Button>
              </div>
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contacts Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Contact</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Platform</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Tags</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Last Interaction</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-gray-900">John Doe</p>
                        <p className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          john@example.com
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Facebook className="h-5 w-5 text-gray-600" />
                        <span className="text-sm text-gray-600">Messenger</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-xs">customer</Badge>
                        <Badge variant="secondary" className="text-xs">vip</Badge>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-gray-600">Jan 15, 2025</span>
                    </td>
                    <td className="p-4">
                      <Badge variant="default" className="bg-green-100 text-green-700">subscribed</Badge>
                    </td>
                    <td className="p-4">
                      <Button variant="ghost" size="sm">View</Button>
                    </td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-gray-900">Jane Smith</p>
                        <p className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          jane@example.com
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Instagram className="h-5 w-5 text-gray-600" />
                        <span className="text-sm text-gray-600">Instagram</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-xs">lead</Badge>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-gray-600">Jan 14, 2025</span>
                    </td>
                    <td className="p-4">
                      <Badge variant="default" className="bg-green-100 text-green-700">subscribed</Badge>
                    </td>
                    <td className="p-4">
                      <Button variant="ghost" size="sm">View</Button>
                    </td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-gray-900">Mike Johnson</p>
                        <p className="text-sm text-gray-500 flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          mike@example.com
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <MessageCircle className="h-5 w-5 text-gray-600" />
                        <span className="text-sm text-gray-600">WhatsApp</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-xs">customer</Badge>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-gray-600">Jan 13, 2025</span>
                    </td>
                    <td className="p-4">
                      <Badge variant="secondary">unsubscribed</Badge>
                    </td>
                    <td className="p-4">
                      <Button variant="ghost" size="sm">View</Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Contacts;
